import {
  createRestyleComponent,
  createVariant,
  color,
  border,
  spacing,
  backgroundColor,
  ColorProps,
  BorderProps,
  SpacingProps,
  BackgroundColorProps,
  VariantProps,
} from '@shopify/restyle';
import React, { useRef, useState } from 'react';
import { StyleSheet, LayoutChangeEvent } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Box, Text } from '@components/Restyle';
import { Spinner } from '@components/shared/Spinner';
import ProjectOverlay from '@components/Tutorialization/ProjectOverlay';
import { useHover } from '@hooks/useHover';
import theme, { Theme, getScaledSize } from '@themes/theme';
import { getButtonVariantHoverBackground } from '@utils/hoverStyles';

type VariantSpec = {
  backgroundColor?: keyof typeof theme.colors;
  borderColor?: keyof typeof theme.colors;
};

/** Resolve a button variant's resting colors from the theme. */
const getVariantSpec = (variant?: string): VariantSpec =>
  ((theme.buttonVariants as any)?.[variant ?? 'defaults'] ?? {}) as VariantSpec;

type RestyleProps = ColorProps<Theme> &
  BorderProps<Theme> &
  SpacingProps<Theme> &
  BackgroundColorProps<Theme> &
  VariantProps<Theme, 'buttonVariants'>;

const RestyleButton = createRestyleComponent<RestyleProps, Theme>([
  color,
  border,
  spacing,
  backgroundColor,
  createVariant({ themeKey: 'buttonVariants' }),
]);

type Props = RestyleProps & {
  fullWidth?: boolean;
  children: React.ReactNode;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  disabled?: boolean;
  alignTextLeft?: boolean;
  centerIcon?: boolean;
  onPress?: () => void;
  prefixMarginRight?: keyof typeof theme.spacing;
  activeOpacity?: number;
  isSmall?: boolean;
  float?: 'bottom' | 'bottom-right' | 'bottom-inset' | 'bottom-right-inset';
  accessibilityLabel?: string;
  width?: number;
  height?: number;
  borderColor?: keyof typeof theme.colors;
  borderWidth?: number;
  testID?: string;
  btnTextColor?: keyof typeof theme.colors;
  isInsetsBottom?: boolean;
  showOverlay?: boolean;
  isLoading?: boolean;
  /**
   * Web only: raw CSS hover background that replaces the auto hover token
   * (e.g. a translucent brand fill instead of the darker greenDark step).
   */
  hoverBackgroundColor?: string;
  // Note: native Button.tsx also accepts `onMeasureWidth` (reports rendered
  // width for callers centering UI over the button, e.g. a tooltip arrow —
  // TA-10919 #8). No web caller needs it today, so it's intentionally not
  // implemented here — but TS won't catch a future caller passing it anyway
  // (this file has its own separate Props type, and there's no
  // `moduleSuffixes` config distinguishing it from Button.tsx for tsc), so
  // it would silently no-op on web. Add `onMeasureWidth` + wire it into
  // `handleLayout` below if a web consumer ever needs it (dev review, PR
  // #5939).
};

const Button: React.FC<Props> = ({
  fullWidth = false,
  disabled = false,
  btnTextColor = undefined,

  children,
  onPress,
  prefix,
  suffix,
  alignTextLeft,
  centerIcon,
  prefixMarginRight,
  isSmall,
  float,
  accessibilityLabel,
  width,
  height,
  borderColor,
  borderWidth,
  testID,
  isInsetsBottom,
  showOverlay,
  isLoading = false,
  hoverBackgroundColor,
  ...rest
}: Props) => {
  const insets = useSafeAreaInsets();
  const { isHovered, hoverProps } = useHover({ disabled });
  const variantSpec = getVariantSpec(
    typeof rest.variant === 'string' ? rest.variant : undefined
  );
  // An explicit `backgroundColor` prop (e.g. the login button's
  // backgroundColor='black' with no variant) wins over the variant's resting
  // background, mirroring how Restyle itself resolves the two.
  const propBackground =
    typeof rest.backgroundColor === 'string'
      ? (rest.backgroundColor as keyof typeof theme.colors)
      : undefined;
  // Some callers hand-roll hover by flipping this prop to a hover colour
  // while the mouse is over the button; latch the value seen while
  // un-hovered so the internal hover doesn't compound it
  // (e.g. white -> grey01 -> grey02 on "Add to Calendar").
  // No explicit `width` prop means the button is sized by its content (e.g.
  // a `float='bottom-right'` FAB) — swapping the label for a narrower
  // spinner would then shrink the button itself. Captured only while NOT
  // loading, then pinned back in during `isLoading` so the footprint stays
  // put instead of visibly resizing (TA-10389 QA finding).
  const [measuredWidth, setMeasuredWidth] = useState<number | undefined>(
    undefined
  );
  const handleLayout = (event: LayoutChangeEvent) => {
    if (!isLoading) setMeasuredWidth(event.nativeEvent.layout.width);
  };
  const restingPropRef = useRef(propBackground);
  if (!isHovered) restingPropRef.current = propBackground;
  const restingBackground =
    restingPropRef.current ?? variantSpec.backgroundColor;
  const hoverBackground = getButtonVariantHoverBackground(restingBackground);
  // Fill buttons (border matches the fill, e.g. primary's black-on-black)
  // shift the border with the background on hover — the Figma button sheet
  // shows one uniform grey05 surface, not a black ring around it. A border
  // counts as part of the fill when it resolves to the same hover color
  // (covers black-border + textPrimary-bg combinations).
  const hoverBorder =
    variantSpec.borderColor &&
    (variantSpec.borderColor === restingBackground ||
      getButtonVariantHoverBackground(variantSpec.borderColor) ===
        hoverBackground)
      ? hoverBackground
      : undefined;
  // Disabled treatment by family (Figma button sheet): fill and tinted
  // buttons flatten to grey02; outline buttons keep their white fill with a
  // grey02 border; ghost buttons (no resting surface) get no surface at all.
  // Text drops to grey04 in every family (`resolvedColor` below).
  const disabledStyle = (() => {
    if (!restingBackground) return null;
    if (restingBackground === 'white')
      return { borderColor: theme.colors.grey02 };
    return {
      backgroundColor: theme.colors.grey02,
      borderColor: theme.colors.grey02,
    };
  })();
  const styleOverrides = fullWidth ? {} : { alignItems: 'center' };
  let color = null;
  let labelVariant = null;

  switch (rest.variant) {
    case 'primary':
      color = 'white';
      labelVariant = 'buttonLabel';
      break;

    case 'webprimary':
      color = 'white';
      labelVariant = 'webBody';
      break;

    case 'text':
      color = 'black';
      break;

    case 'textweb':
      color = 'black';
      labelVariant = 'webBody';
      break;

    case 'textSecondary':
      color = 'greenSecondary';
      break;

    case 'create':
      color = 'white';
      labelVariant = 'taskBtnLabel';
      break;

    case 'copy':
      color = 'black';
      labelVariant = 'taskBtnLabel';
      break;

    case 'pending':
      color = 'grey04';
      labelVariant = 'taskBtnLabel';
      break;

    case 'edit':
      color = 'textPrimary';
      labelVariant = 'labelSmall';
      break;

    case 'editWhite':
      color = 'white';
      labelVariant = 'labelSmall';
      break;

    case 'editError':
      color = 'alertRed';
      labelVariant = 'labelSmall';
      break;

    case 'editBold':
      color = 'textPrimary';
      labelVariant = 'buttonLabel';
      break;

    case 'editSelected':
      color = 'textPrimary';
      labelVariant = 'labelSmall';
      break;

    case 'editSelectedDisabled':
      color = 'grey04';
      labelVariant = 'labelSmall';
      break;

    case 'editSelectedDisabledDark':
      color = 'grey05';
      labelVariant = 'labelSmall';
      break;

    case 'reversed':
      color = 'black';
      labelVariant = 'buttonLabel';
      break;

    case 'icon':
      color = 'white';
      labelVariant = null;
      break;

    case 'delete':
      color = 'alertRed';
      labelVariant = 'buttonLabel';
      break;

    case 'logout':
      color = 'alertRed';
      labelVariant = 'webSmall';
      break;

    case 'upload':
      color = 'textPrimary';
      labelVariant = 'labelSmall';
      break;

    case 'greenSecondary':
      color = 'white';
      labelVariant = 'labelEmphasized';
      break;

    default:
      color = 'black';
      labelVariant = 'bodySecondary';
  }

  const renderButton = () => {
    const height1 = height ? getScaledSize(height) : getScaledSize(48);
    // `disabled` drives the visual "disabled" look (grey background/text) —
    // that's an author-controlled state. `isLoading` only locks the click
    // and swaps the label for a spinner; it deliberately keeps the button's
    // normal color so the spinner reads as "processing", not "disabled".
    const isClickLocked = disabled || isLoading;
    const resolvedColor = btnTextColor ?? (disabled ? 'grey04' : color);

    return (
      <button
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          margin: 0,
          cursor: isClickLocked ? 'default' : 'pointer',
          width: fullWidth ? '100%' : undefined,
          font: 'inherit',
          textAlign: 'inherit' as const,
          display: 'block',
        }}
        disabled={isClickLocked}
        onClick={onPress}
        onMouseEnter={hoverProps.onMouseEnter}
        onMouseLeave={hoverProps.onMouseLeave}
        data-testid={testID}
        aria-label={accessibilityLabel}
        type='button'>
        <RestyleButton
          {...(rest as any)}
          onLayout={handleLayout}
          style={[
            styleOverrides,
            disabled && disabledStyle,
            !disabled &&
              isHovered &&
              !hoverBackgroundColor &&
              hoverBackground && {
                backgroundColor: theme.colors[hoverBackground],
                ...(hoverBorder
                  ? { borderColor: theme.colors[hoverBorder] }
                  : {}),
              },
            !disabled &&
              isHovered &&
              hoverBackgroundColor && {
                backgroundColor: hoverBackgroundColor,
              },
            borderColor && {
              borderColor: borderColor,
              borderWidth: 1,
            },
            borderWidth !== undefined && {
              borderWidth: borderWidth,
            },
            {
              justifyContent: 'center',
              height: isSmall ? height1 : getScaledSize(56),
              width: width ?? (isLoading ? measuredWidth : undefined),
            },
          ]}>
          <ProjectOverlay borderRadius='xl' visible={!!showOverlay} />
          {isLoading ? (
            // Icon and label both drop out in favor of one centered
            // spinner — showing the icon alongside the spinner read as
            // cluttered (two circular shapes), and `measuredWidth` above
            // already keeps the button from resizing when content swaps
            // (TA-10389 QA finding/discussion).
            <Box
              testID='button-spinner'
              alignItems='center'
              justifyContent='center'
              style={{ flex: 1 }}>
              <Spinner
                size={20}
                strokeWidth={2}
                color={theme.colors[resolvedColor as keyof Theme['colors']]}
              />
            </Box>
          ) : (
            <>
              {prefix && (
                <Box
                  width={16}
                  height={16}
                  marginRight={centerIcon ? 'none' : prefixMarginRight || 's'}
                  alignItems='center'
                  justifyContent='center'
                  pointerEvents='none'>
                  {prefix}
                </Box>
              )}
              {!centerIcon && (
                <Box
                  alignItems={alignTextLeft ? 'flex-start' : 'center'}
                  justifyContent='center'
                  style={(!prefix || alignTextLeft) && { flex: 1 }}>
                  <Text
                    variant={labelVariant as any}
                    color={resolvedColor as any}
                    style={[
                      alignTextLeft && {
                        marginLeft: theme.spacing.m,
                      },
                    ]}>
                    {children}
                  </Text>
                </Box>
              )}
              {suffix && !centerIcon && (
                <Box
                  width={theme.spacing.m}
                  height={theme.spacing.m}
                  marginLeft='xs'
                  pointerEvents='none'>
                  {suffix}
                </Box>
              )}
            </>
          )}
        </RestyleButton>
      </button>
    );
  };

  const floatBottomRight = () => {
    return (
      <Box
        style={{
          position: 'absolute',
          bottom:
            theme.spacing.m +
            (isInsetsBottom ? insets.bottom - theme.spacing.m : 0),
          right: theme.spacing.m,
        }}>
        {renderButton()}
      </Box>
    );
  };

  const floatBottomInset = () => {
    return (
      <Box
        style={[
          styles.submitButton,
          { bottom: insets.bottom ? theme.spacing.s : 0 },
        ]}
        marginBottom='m'
        marginHorizontal='m'>
        {renderButton()}
      </Box>
    );
  };

  const floatBottomRightInset = () => {
    return (
      <Box
        style={{
          position: 'absolute',
          bottom: insets.bottom ? theme.spacing.xl : theme.spacing.l,
          right: theme.spacing.m,
        }}>
        {renderButton()}
      </Box>
    );
  };

  if (float === 'bottom-right') return floatBottomRight();
  if (float === 'bottom-inset') return floatBottomInset();
  if (float === 'bottom-right-inset') return floatBottomRightInset();

  return renderButton();
};

const styles = StyleSheet.create({
  submitButton: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
});

export default Button;
